import { useCallback, useEffect, useRef } from "react"
import { DATAFILE_TYPE } from "types/dataImport.types"
import type {
  EngineQueryResult,
  HierarchicalDataEntry,
} from "types/engineOutput.types"
import { useDataImport } from "features/DataImport/api/useDataImport"
import { extractEngineError } from "features/Dashboard/runtime/engineRunner"
import {
  numericVariables,
  weightsLine,
} from "features/Dashboard/runtime/prepScript"
import { useProjectStore } from "stores/projectStore"
import {
  readInitialiseReport,
  runOnLoadFailureText,
} from "types/runOnLoad.types"
import handleSequenceError from "utils/handleSequenceError"
import { TOTAL_PREP } from "../compiler/names"
import type { TabSpecDocument } from "../types/tabSpecDocument"
import {
  preparationParts,
  PrepSource,
  SOURCES_NOT_LOADED,
} from "./preparation"
import { useTabSpecStore, useTabSpecStoreApi } from "./tabSpecStore"
import { useEngineCommands } from "./useEngineCommands"

const messageOf = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback

/** What the preparation is made of: a change starts a new session. */
const prepKeyOf = (data: TabSpecDocument["data"]): string =>
  `${JSON.stringify(data.pipelines)}|${JSON.stringify(data.weight)}`

/**
 * Owns the engine session of one mounted tab spec: initialise-data, then
 * RESET, the preparation (the spec's pipelines, scripts and weight), the
 * Total table every banner-less table uses, and CODEBOOK. `readSources`
 * reads the chosen saved pipelines and scripts as they are at that moment;
 * it is called at every preparation that chooses any, so a body saved in
 * the console since is the one that runs, and a list that could not be read
 * is read again (the preparation then says it could not be loaded). A
 * failed preparation leaves the session open in "prep-error" with the
 * codebook loaded, so the spec stays usable for fixing it.
 *
 * `restart` opens a new instance every time, which is what makes a session
 * the engine has reaped recoverable. A change of the spec's preparation or
 * weight keeps the instance (each one holds a copy of the data): it sends
 * RESET and the rest of the sequence again on it, as a new session
 * generation. Without an instance yet it does the whole start.
 */
export const useTabSpecSession = (
  readSources: (chosen: string[]) => Promise<PrepSource[]>
): { restart: () => Promise<void> } => {
  const store = useTabSpecStoreApi()
  const project = useProjectStore((s) => s.project)
  const { initialiseData, RunEngineQuery } = useEngineCommands()
  const { getDataFilesUploaded } = useDataImport()
  const startedFor = useRef<string | null>(null)
  /** Bumped by every sequence; an older one that is still awaiting stops writing. */
  const sequence = useRef(0)
  /** Whether a sequence is under way; a preparation change waits for its end. */
  const busy = useRef(false)
  /** Whether a sequence has run to its end; before that a preparation change waits for it. */
  const settled = useRef(false)
  /** The preparation the latest sequence was given. */
  const prepUsed = useRef<string | null>(null)
  /** The scripts the Conductor ran on load into the current instance: the preparation skips them. */
  const onLoadRef = useRef<string[]>([])
  /** The latest reader of the saved pipelines and scripts; a new one never prepares again. */
  const readRef = useRef(readSources)
  useEffect(() => {
    readRef.current = readSources
  }, [readSources])

  /**
   * One session sequence. `fresh` opens a new instance (the first start and
   * `restart`); otherwise the current instance is prepared again, or, when
   * there is none yet, the whole start runs.
   */
  const begin = useCallback(async (fresh: boolean): Promise<void> => {
    const mine = ++sequence.current
    busy.current = true
    const overtaken = (): boolean => sequence.current !== mine
    const { document, session, setSession, beginSession, setCodebook } =
      store.getState()
    prepUsed.current = prepKeyOf(document.data)
    const fail = (error: string): void => setSession({ status: "error", error })
    setSession({ status: "starting", error: undefined, prepError: undefined })
    try {
      let instance = fresh ? null : session.instance
      if (!instance) {
        const size = await getDataFilesUploaded(
          project.id,
          project.Created_by,
          DATAFILE_TYPE.DQD,
          "size"
        )
        if (overtaken()) return
        // Not an error: the spec can be set up before the data is imported.
        if (typeof size.data !== "number") {
          setSession({ status: "no-data" })
          return
        }
        const init = await initialiseData(project.id, "report")
        if (overtaken()) return
        const report = readInitialiseReport(init.data)
        if (!report) {
          handleSequenceError(init.data, "Error initialising data")
          fail("The engine could not open the project data.")
          return
        }
        // No numbers from a half-prepared dataset (spec R4). The instance is left
        // to the engine's idle timeout: no user route closes one.
        if (report.failed) {
          fail(runOnLoadFailureText(report.failed))
          return
        }
        onLoadRef.current = report.applied
        instance = report.instance
      }
      // A new generation: the known banners and every run are forgotten, and
      // the queue of the previous one stops sending.
      beginSession(instance)

      const reset = await RunEngineQuery(instance, "RESET", false)
      if (overtaken()) return
      const resetError = extractEngineError(reset.data)
      if (resetError) {
        fail(`The data could not be reset: ${resetError}`)
        return
      }

      // The preparation runs part by part, so a failure names its pipeline,
      // script or line; it stops at the first failing part and never keeps
      // the codebook from loading. When the saved pipelines and scripts
      // cannot be read, or a chosen one is missing, empty or refused (see
      // `preparationParts`), it does not run at all.
      const chosen = document.data.pipelines
      let sources: PrepSource[] = []
      let unread = false
      if (chosen.length > 0) {
        try {
          sources = await readRef.current(chosen)
        } catch {
          unread = true
        }
        if (overtaken()) return
      }
      const { parts, problem } = preparationParts({
        sources,
        chosen,
        weight: document.data.weight,
        onLoad: onLoadRef.current,
      })
      let prepError: string | null = unread ? SOURCES_NOT_LOADED : problem
      for (const part of prepError ? [] : parts) {
        const ran = await RunEngineQuery(instance, part.script, false)
        if (overtaken()) return
        const failed = extractEngineError(ran.data)
        if (failed) {
          prepError = `${part.label}: ${failed}`
          break
        }
      }

      const total = await RunEngineQuery(instance, TOTAL_PREP, false)
      if (overtaken()) return
      const totalError = extractEngineError(total.data)
      if (totalError) {
        fail(`The Total column could not be set up: ${totalError}`)
        return
      }

      const cb = await RunEngineQuery(instance, "CODEBOOK", false)
      if (overtaken()) return
      if (!handleSequenceError(cb.data, "Error creating codebook")) {
        fail("Could not load the codebook.")
        return
      }
      const output = (cb.data as EngineQueryResult).result?.output?.[0]
      const value =
        output && output.content && "value" in output.content
          ? (output.content as { value: unknown }).value
          : null
      const codebook = Array.isArray(value)
        ? (value as HierarchicalDataEntry[])
        : null
      setCodebook(codebook)
      // The engine takes any name in USEWEIGHTS and fails only in the tables
      // that use it, so the weight is checked against the data here.
      const weight = document.data.weight
      if (
        !prepError &&
        weight &&
        codebook &&
        !numericVariables(codebook).includes(weight)
      ) {
        prepError = `${weightsLine(weight)}: ${weight} is not a numeric variable in the data.`
      }
      setSession(
        prepError
          ? { status: "prep-error", error: undefined, prepError, preparation: parts }
          : { status: "ready", error: undefined, prepError: undefined, preparation: parts }
      )
    } catch (e) {
      if (overtaken()) return
      fail(messageOf(e, "Could not start the session."))
    } finally {
      if (!overtaken()) {
        busy.current = false
        settled.current = true
        // The preparation changed while this sequence ran: it is out of date.
        if (prepKeyOf(store.getState().document.data) !== prepUsed.current)
          void begin(false)
      }
    }
  }, [
    store,
    project.id,
    project.Created_by,
    getDataFilesUploaded,
    initialiseData,
    RunEngineQuery,
  ])

  // One initialise-data per mounted spec: every call creates an engine
  // instance holding a copy of the data.
  useEffect(() => {
    if (!project.id || startedFor.current === project.id) return
    startedFor.current = project.id
    void begin(true)
  }, [project.id, begin])

  // A change of the preparation or the weight prepares the data again once the
  // first start has finished. A change made while a sequence runs is picked
  // up at its end.
  const prep = useTabSpecStore((s) => prepKeyOf(s.document.data))
  useEffect(() => {
    if (!settled.current || busy.current || prep === prepUsed.current) return
    void begin(false)
  }, [prep, begin])

  const restart = useCallback(async (): Promise<void> => {
    // Before the first start there is nothing to restart: it starts once
    // the project is known.
    if (startedFor.current !== project.id) return
    // The preparation reads the list only when something is chosen; read it
    // here too, so a list that failed can be offered in the Data drawer.
    if (store.getState().document.data.pipelines.length === 0) void readRef.current([]).catch(() => {})
    await begin(true)
  }, [store, project.id, begin])

  return { restart }
}
